import { ethers } from "ethers";
import { useEffect, useState } from "react";

import { useWeb3React } from "@web3-react/core";

import {
  useGnarlyGoatsContracts,
  useGnarlyGoatsContractsState
} from "../../hooks/useGnarlyGoatsContracts";
import { etherscanUrl } from "../../hooks/useHelpers";
import { useMintGoats } from "../../hooks/useMintGoats";
import { useSale } from "../../hooks/useSale";
import { useWhitelist } from "../../hooks/useWhitelist";
import {
  Button,
  DashboardWrapper,
  ExternalLink,
  InputBox,
  Interface,
  PageWrapper,
  Text,
  Title
} from "../../theme/components";
import { Copy } from "../../theme/icons";

const OwnerPage = (props: any) => {
	const { account, chainId, library } = useWeb3React()

	const { contract, mintPassContract } = useGnarlyGoatsContracts()

	const {
		isOwner,
		baseUri,
		updateBaseUri,
		mintPassContractBaseUri,
		updateMintPassContractBaseUri,
	} = useGnarlyGoatsContractsState()

	const { mintAmount, setMintAmount } = useMintGoats()

	const {
		saleIsActive,
		setSaleIsActive,
		mintPassSaleIsActive,
		setMintPassSaleIsActive,
	} = useSale()

	const { merkleRoot, updateMerkleRoot } = useWhitelist()

	const [newBaseURI, setNewBaseURI] = useState<string>()
	const [newMintPassBaseURI, setNewMintPassBaseURI] = useState<string>()

	const [toAddress, setToAddress] = useState<string>()
	const [tokenId, setTokenId] = useState<number>()

	const [amountToReplenish, setAmountToReplenish] = useState<number>(25)
	const [amountToReserve, setAmountToReserve] = useState<number>(1)

	const [contractEthBalance, setContractEthBalance] = useState<number>(0)
	const [mintPassEthBalance, setMintPassEthBalance] = useState<number>(0)

	// represents the address that is currently stored within the Mint Pass contract
	const [gnarlyGoatsContract, setGnarlyGoatsContract] = useState<string>()

	useEffect(() => {
		async function initState() {
			// retrieve the ETH balances of both contracts
			setContractEthBalance(await library.getBalance(contract?.address))
			setMintPassEthBalance(await library.getBalance(mintPassContract?.address))

			// retrieve the address of the gnarlyGoatsContract variable in the Mint Pass
			setGnarlyGoatsContract(await mintPassContract?.gnarlyGoatsContract())
		}

		initState()
	}, [])

	const withdraw = async () => {
		try {
			if (isOwner) {
				await contract?.withdraw()
			}
		} catch (err) {}
	}

	async function withdrawMintPass() {
		try {
			if (isOwner) {
				await mintPassContract?.withdrawETH(account)
			}
		} catch (err) {}
	}

	const reserveGoats = async () => {
		try {
			if (isOwner) {
				await contract?.reserveGoats()
			}
		} catch (err) {}
	}

	const switchContractSaleState = async () => {
		if (isOwner) {
			try {
				await contract?.switchSaleState()
				setSaleIsActive(true)
			} catch (err) {
				console.error('Error switching sale state.')
			}
		} else {
			console.error('Only owner can switch sale state.')
		}
	}

	const switchMintPassContractSaleState = async () => {
		if (isOwner) {
			try {
				await mintPassContract?.switchSaleState()
				setMintPassSaleIsActive(true)
			} catch (err) {
				console.error('Error switching mint pass sale state.')
			}
		} else {
			console.error('Only owner can switch sale state.')
		}
	}

	const replenishMintPasses = async (amount: number) => {
		if (isOwner) {
			try {
				await mintPassContract?.replenishMintPasses(amount)
			} catch (err) {
				console.log('Error replenishing mint passes.')
			}
		}
	}

	const reserveMintPasses = async (amount: number) => {
		if (isOwner) {
			try {
				await mintPassContract?.reserveMintPasses(amount)
			} catch (err) {
				console.log('Error reserving mint passes.')
			}
		}
	}

	const setStartingIndex = async () => {
		try {
			await contract?.setStartingIndex()
		} catch (err) {}
	}

	const getRandomNumber = async () => {
		try {
			await contract?.getRandomNumber()
		} catch (err) {}
	}

	const sendTokenTo = async (to: string, tokenId: number) => {
		console.log(`Sending token ${tokenId} to ${to}`)
		const ownerBalance = await contract?.balanceOf(account)
		console.log(ownerBalance.toNumber(0))
		if (ownerBalance.toNumber() > 0) {
			try {
				const ownerOfToken = await contract?.ownerOf(tokenId)
				if (ownerOfToken !== account) {
					throw new Error('not allowed to send token')
				}
				const toAddress = ethers.utils.getAddress(to)
				console.log(to, toAddress)
				await contract?.transferFrom(account, toAddress, tokenId)
			} catch (err) {
				console.error(err)
			}
		}
	}

	async function setGnarlyGoatsContractOfMintPassContract(
		address: string | undefined
	) {
		console.log(address)
		await mintPassContract?.setGnarlyGoatsContract(address)
	}

	return (
		<PageWrapper>
			<DashboardWrapper>
				<Title>Gnarly Goats Contract</Title>

				<Text>
					<ExternalLink
						href={etherscanUrl(contract?.address)}
						target={'_blank'}>
						{contract?.address}
					</ExternalLink>
					<Copy
						size={24}
						onClick={() => {
							navigator.clipboard.writeText(contract?.address ?? '')
						}}></Copy>
				</Text>

				<Interface>
					<Button owner onClick={async () => await getRandomNumber()}>
						Get Random Number
					</Button>
					<Button owner onClick={async () => await setStartingIndex()}>
						Set Starting Index
					</Button>
				</Interface>

				<Interface>
					<Button owner onClick={async () => await switchContractSaleState()}>
						{saleIsActive ? 'Stop ' : 'Start '}Sale
					</Button>
					<Button owner onClick={async () => await withdraw()}>
						{`Withdraw ${ethers.utils.formatEther(contractEthBalance)} ETH`}
					</Button>
					<Button owner onClick={async () => await reserveGoats()}>
						Reserve Goats
					</Button>
				</Interface>

				<Interface>
					<InputBox
						grow
						id="base-uri-input"
						type="url"
						defaultValue={baseUri}
						onChange={(e: any) => {
							setNewBaseURI(e.target.value)
						}}
					/>
					<Button
						owner
						onClick={async () => {
							if (newBaseURI) {
								await updateBaseUri(newBaseURI)
							}
						}}>
						Set Base URI
					</Button>

					<Button onClick={() => window.open(baseUri, '_blank')}>
						View Resource
					</Button>
				</Interface>

				<Text>{`Merkle Root: ${merkleRoot}`}</Text>
				<Interface>
					<Button owner onClick={async () => await updateMerkleRoot()}>
						Set Merkle Root
					</Button>
				</Interface>

				<br></br>

				<Title>Send Reserved Tokens</Title>

				<Interface justifyContent={'space-between'}>
					<Text>{'Address To: '}</Text>
					<InputBox
						grow
						type="text"
						onChange={(e: any) => {
							setToAddress(e.target.value)
						}}></InputBox>
				</Interface>

				<Interface justifyContent={'space-between'}>
					<Text>{'Tokend Id: '}</Text>
					<InputBox
						type="number"
						defaultValue={0}
						onChange={(e: any) => setTokenId(e.target.value)}></InputBox>
				</Interface>

				<Button
					owner
					disabled={!toAddress || !tokenId}
					onClick={() =>
						toAddress && tokenId && sendTokenTo(toAddress, tokenId)
					}>
					Send Tokens
				</Button>
			</DashboardWrapper>

			<DashboardWrapper>
				<Title>Mint Pass Contract</Title>

				<Text>
					<ExternalLink
						href={etherscanUrl(mintPassContract?.address)}
						target={'_blank'}>
						{mintPassContract?.address}
					</ExternalLink>

					<Copy
						size={24}
						onClick={() => {
							navigator.clipboard.writeText(mintPassContract?.address ?? '')
						}}></Copy>
				</Text>

				<Interface>
					<Button
						owner
						onClick={async () => await switchMintPassContractSaleState()}>
						{mintPassSaleIsActive ? 'Stop ' : 'Start '}Sale
					</Button>

					<Button owner onClick={async () => await withdrawMintPass()}>
						{`Withdraw ${ethers.utils.formatEther(mintPassEthBalance)} ETH`}
					</Button>
				</Interface>

				<Interface>
					<InputBox
						grow
						id="mint-pass-base-uri-input"
						type="url"
						defaultValue={mintPassContractBaseUri}
						onChange={(e: any) => {
							setNewMintPassBaseURI(e.target.value)
						}}
					/>

					<Button
						owner
						onClick={async () => {
							if (newMintPassBaseURI) {
								await updateMintPassContractBaseUri(newMintPassBaseURI)
							}
						}}>
						Set Base URI
					</Button>

					<Button
						onClick={() => window.open(mintPassContractBaseUri, '_blank')}>
						View Resource
					</Button>
				</Interface>

				<Interface>
					<InputBox
						defaultValue={amountToReplenish}
						type="number"
						onChange={(e: any) => {
							setAmountToReplenish(e.target.value)
						}}></InputBox>

					<Button
						owner
						onClick={async () => await replenishMintPasses(amountToReplenish)}>
						{`Replenish ${amountToReplenish} Passes`}
					</Button>
				</Interface>

				<Interface>
					<InputBox
						defaultValue={amountToReserve}
						type="number"
						onChange={(e: any) => {
							setAmountToReserve(e.target.value)
						}}></InputBox>

					<Button
						owner
						onClick={async () => await reserveMintPasses(amountToReserve)}>
						{`Reserve ${amountToReserve} Passes`}
					</Button>
				</Interface>

				<Interface>
					<InputBox
						defaultValue={gnarlyGoatsContract}
						type="text"
						onChange={(e: any) => {
							setGnarlyGoatsContract(e.target.value)
						}}></InputBox>

					<Button
						owner
						onClick={async () =>
							await setGnarlyGoatsContractOfMintPassContract(
								gnarlyGoatsContract
							)
						}>
						Set Gnarly Goats Contract
					</Button>
				</Interface>
			</DashboardWrapper>
		</PageWrapper>
	)
}

export default OwnerPage
